import { symbolsJson } from "@corca/data";
import { sortBy } from "es-toolkit";

import {
  librarySuggestionByGlyph,
  librarySuggestionBySymbolKey,
  librarySymbolSuggestions,
} from "../librarySuggestionsProvider/existing";

import { appendLatexKeywords } from "./appendLatexKeywords";
import { filterSuggestions, prioritizeSuggestions } from "./filterSuggestions";
import { normalizeSuggestionKeywords } from "./normalizeKeywords";

import type { SingleSuggestion } from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeLookupModel } from "@/models/NodeLookupModel";
import type { KeywordLang, LocaleKeywords } from "@corca/data";

/**
 * What a symbol is, for the typed providers' own rows. The library provider offers the symbol
 * rows themselves; the value-node and plain-text providers only ask these four questions about
 * the catalogue while building their "new" and "existing" rows.
 */
export type SymbolLookup = {
  /**
   * The symbol typed as its glyph, so a new node gets the symbol's key and keywords.
   */
  byGlyph(glyph: string): { key: string; keywords: LocaleKeywords } | undefined;
  /**
   * The symbol an existing node's symbol child points at, so the node is matched by the
   * symbol's keywords.
   */
  byKey(key: string): { keywords: LocaleKeywords } | undefined;
  /**
   * The symbol whose name best matches the input, so `alpha` names a new node α. `filter` narrows
   * the candidates, as the constant provider does to the Greek alphabet.
   */
  bestMatch(
    input: string,
    options: {
      lang: KeywordLang;
      nodeLookup: NodeLookupModel | null;
      filter?: (suggestion: SingleSuggestion) => boolean;
    },
  ): SingleSuggestion | undefined;
  /**
   * The keywords of weight above zero that name a glyph, so an existing α is matched by `alp`.
   */
  keywordsOf(glyph: string): string[];
};

/**
 * The `symbols.json` rows as the variable provider typed them, until the file is deleted.
 */
export const symbolVariables = symbolsJson.map(
  (s) =>
    ({
      type: "single",
      item: {
        ...s,
        type: "variable",
        keywords: appendLatexKeywords(s.key, normalizeSuggestionKeywords(s)),
        intlName: "variable",
        displayName: s.name,
      },
      sidebarGroupKey: s.sidebarGroupKey,
      priority: s.priority ?? 0.9,
      context: s.context ?? ["single"],
      popupGroup: "symbols",
    }) as SingleSuggestion<"variable">,
);

export const greekLetterVariables = symbolVariables.filter(
  (v) => v.item.category === "Greek alphabet",
);

function namedKeywords(suggestion: SingleSuggestion): string[] {
  const keywords = suggestion.item.keywords?.en ?? {};
  return Object.keys(keywords).filter((keyword) => keywords[keyword] > 0);
}

function bestMatchIn(
  symbols: SingleSuggestion[],
  input: string,
  { lang, nodeLookup, filter = () => true }: Parameters<SymbolLookup["bestMatch"]>[1],
): SingleSuggestion | undefined {
  const matched = filterSuggestions(input, symbols, { lang, skipPrioritize: true }).filter(filter);
  const [best] = sortBy(prioritizeSuggestions(input, matched, nodeLookup, lang), [
    (suggestion) => -suggestion.priority,
  ]);

  return best && matched.find((suggestion) => suggestion.item.key === best.item.key);
}

function lookupOver(
  symbols: SingleSuggestion[],
  byGlyph: (glyph: string) => SingleSuggestion | undefined,
  byKey: (key: string) => SingleSuggestion | undefined,
): SymbolLookup {
  const keywordsByGlyph = new Map<string, string[]>();
  for (const suggestion of symbols) {
    const glyph = suggestion.item.symbol;
    keywordsByGlyph.set(glyph, [
      ...(keywordsByGlyph.get(glyph) ?? []),
      ...namedKeywords(suggestion),
    ]);
  }

  return {
    byGlyph: (glyph) => {
      const { item } = byGlyph(glyph) ?? {};
      return item?.key && item.keywords ? { key: item.key, keywords: item.keywords } : undefined;
    },
    byKey: (key) => {
      const { item } = byKey(key) ?? {};
      return item?.keywords ? { keywords: item.keywords } : undefined;
    },
    bestMatch: (input, options) => bestMatchIn(symbols, input, options),
    keywordsOf: (glyph) => keywordsByGlyph.get(glyph) ?? [],
  };
}

const fileLookup = lookupOver(
  symbolVariables,
  (glyph) => symbolVariables.find((s) => s.item.symbol === glyph),
  (key) => symbolVariables.find((s) => s.item.key === key),
);

const libraryLookups = new WeakMap<SingleSuggestion[], SymbolLookup>();

/**
 * The symbol lookup for a document: over the served library symbols once `symbols.json` is
 * served, over the file until then. Built once per served items, like the provider's caches.
 */
export function symbolLookupFor(doc: DocumentModel): SymbolLookup {
  if (!doc.libraries.serves("symbols.json")) {
    return fileLookup;
  }

  const symbols = librarySymbolSuggestions(doc);
  let lookup = libraryLookups.get(symbols);

  if (!lookup) {
    lookup = lookupOver(
      symbols,
      (glyph) => librarySuggestionByGlyph(doc, glyph),
      (key) => librarySuggestionBySymbolKey(doc, key),
    );
    libraryLookups.set(symbols, lookup);
  }

  return lookup;
}
